/**
 * Registry metadata filter bar for Knowledge Base artifacts
 */

import React from 'react';
import { Badge } from '../UI/Badge';
import { Button } from '../UI/Button';
import { Filter, X } from 'lucide-react';
import {
  DEFAULT_REGISTRY_FILTERS,
  REGISTRY_GROUP_BY_OPTIONS,
  formatDocTypeLabel,
  type RegistryFilterOptions,
  type RegistryFilters,
  type RegistryGroupBy,
  countActiveRegistryFilters,
} from '../../utils/registryArtifactUtils';

interface RegistryFilterBarProps {
  filters: RegistryFilters;
  options: RegistryFilterOptions;
  groupBy: RegistryGroupBy;
  registryArtifactCount: number;
  onFiltersChange: (filters: RegistryFilters) => void;
  onGroupByChange: (groupBy: RegistryGroupBy) => void;
  onClear: () => void;
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  formatOption,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  formatOption?: (value: string) => string;
}) {
  return (
    <div className="min-w-[140px] flex-1">
      <label className="mb-1 block text-xs font-medium text-theme-text-secondary">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-theme-border bg-theme-paper px-3 py-2 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-black"
      >
        <option value="all">All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {formatOption ? formatOption(opt) : opt}
          </option>
        ))}
      </select>
    </div>
  );
}

export function RegistryFilterBar({
  filters,
  options,
  groupBy,
  registryArtifactCount,
  onFiltersChange,
  onGroupByChange,
  onClear,
}: RegistryFilterBarProps) {
  const activeCount = countActiveRegistryFilters(filters);

  const update = (patch: Partial<RegistryFilters>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  return (
    <div className="space-y-3 border-t border-theme-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-semibold text-theme-text">Registry classification</span>
          <Badge variant="info" size="sm">
            {registryArtifactCount} with registry meta
          </Badge>
          {activeCount > 0 && (
            <Badge variant="primary" size="sm">
              {activeCount} filter{activeCount !== 1 ? 's' : ''} active
            </Badge>
          )}
        </div>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="mr-1 h-3 w-3" />
            Clear registry filters
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <FilterSelect
          label="Project ID"
          value={filters.projectId}
          options={options.projectIds}
          onChange={(projectId) => update({ projectId })}
        />
        <FilterSelect
          label="Keyword tag"
          value={filters.tag}
          options={options.tags}
          onChange={(tag) => update({ tag })}
        />
        <FilterSelect
          label="Doc type"
          value={filters.docType}
          options={options.docTypes}
          onChange={(docType) => update({ docType })}
          formatOption={formatDocTypeLabel}
        />
        <FilterSelect
          label="Client"
          value={filters.client}
          options={options.clients}
          onChange={(client) => update({ client })}
        />
        <FilterSelect
          label="Geography"
          value={filters.geography}
          options={options.geographies}
          onChange={(geography) => update({ geography })}
        />
        <FilterSelect
          label="Service line"
          value={filters.serviceLine}
          options={options.serviceLines}
          onChange={(serviceLine) => update({ serviceLine })}
        />
        <FilterSelect
          label="Registry source"
          value={filters.registrySource}
          options={options.registrySources}
          onChange={(registrySource) => update({ registrySource })}
        />
        <div className="min-w-[160px] flex-1">
          <label className="mb-1 block text-xs font-medium text-theme-text-secondary">Group by</label>
          <select
            value={groupBy}
            onChange={(e) => onGroupByChange(e.target.value as RegistryGroupBy)}
            className="w-full rounded-lg border border-theme-border bg-theme-paper px-3 py-2 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-black"
          >
            {REGISTRY_GROUP_BY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export { DEFAULT_REGISTRY_FILTERS };
